import React from "react";

// Lightweight Markdown renderer for AI output (no raw ##, **, ---, | left visible).
// Handles headings, bold/italic, inline code, links, bullet/numbered lists, tables,
// horizontal rules and paragraphs. Styled for the dark premium theme.

function renderInline(text, keyBase) {
  const nodes = [];
  const rx = /(\*\*([^*]+)\*\*|__([^_]+)__|\*([^*]+)\*|_([^_]+)_|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\))/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = rx.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[2] !== undefined || m[3] !== undefined) {
      nodes.push(<strong key={`${keyBase}-b${i}`} className="font-semibold text-white">{m[2] ?? m[3]}</strong>);
    } else if (m[4] !== undefined || m[5] !== undefined) {
      nodes.push(<em key={`${keyBase}-i${i}`}>{m[4] ?? m[5]}</em>);
    } else if (m[6] !== undefined) {
      nodes.push(<code key={`${keyBase}-c${i}`} className="px-1.5 py-0.5 rounded bg-white/10 text-neon text-[0.85em]">{m[6]}</code>);
    } else if (m[7] !== undefined) {
      nodes.push(<a key={`${keyBase}-a${i}`} href={m[8]} target="_blank" rel="noreferrer" className="text-neon underline">{m[7]}</a>);
    }
    last = m.index + m[0].length;
    i += 1;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export function Markdown({ children, className = "" }) {
  const src = String(children || "");
  const lines = src.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;
  let k = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) { i += 1; continue; }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
      blocks.push(<hr key={`hr${k++}`} className="my-3 border-white/10" />);
      i += 1;
      continue;
    }

    // Heading
    const h = /^(#{1,6})\s+(.*)$/.exec(trimmed);
    if (h) {
      const level = h[1].length;
      const size = level <= 1 ? "text-lg font-display font-bold" : level === 2 ? "text-base font-display font-bold" : "text-sm font-semibold";
      blocks.push(<p key={`h${k++}`} className={`${size} text-white mt-3 mb-1`}>{renderInline(h[2], `h${k}`)}</p>);
      i += 1;
      continue;
    }

    // Table (consecutive lines starting with |)
    if (/^\|.*\|/.test(trimmed) && i + 1 < lines.length && /^\|?\s*:?-{2,}/.test(lines[i + 1].trim())) {
      const tbl = [];
      while (i < lines.length && /^\|.*\|/.test(lines[i].trim())) { tbl.push(lines[i].trim()); i += 1; }
      const parseRow = (r) => r.replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
      const header = parseRow(tbl[0]);
      const rows = tbl.slice(2).map(parseRow);
      blocks.push(
        <div key={`t${k++}`} className="my-3 overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-xs">
            <thead><tr className="bg-white/5">{header.map((c, ci) => <th key={ci} className="text-left px-3 py-2 font-semibold text-white">{renderInline(c, `th${ci}`)}</th>)}</tr></thead>
            <tbody>{rows.map((row, ri) => <tr key={ri} className="border-t border-white/5">{row.map((c, ci) => <td key={ci} className="px-3 py-2 align-top">{renderInline(c, `td${ri}${ci}`)}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
      continue;
    }

    // Unordered list
    if (/^\s*[-*+]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*+]\s+/, ""));
        i += 1;
      }
      blocks.push(
        <ul key={`ul${k++}`} className="my-2 space-y-1 pl-1">
          {items.map((it, ii) => (
            <li key={ii} className="flex gap-2 text-sm leading-relaxed">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-neon shrink-0" />
              <span className="flex-1">{renderInline(it, `li${ii}`)}</span>
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
        i += 1;
      }
      blocks.push(
        <ol key={`ol${k++}`} className="my-2 space-y-1 pl-1">
          {items.map((it, ii) => (
            <li key={ii} className="flex gap-2 text-sm leading-relaxed">
              <span className="text-neon font-semibold shrink-0">{ii + 1}.</span>
              <span className="flex-1">{renderInline(it, `oli${ii}`)}</span>
            </li>
          ))}
        </ol>
      );
      continue;
    }

    // Paragraph (accumulate until blank line or block start)
    const para = [];
    while (i < lines.length) {
      const l = lines[i];
      const t = l.trim();
      if (!t || /^(#{1,6})\s+/.test(t) || /^\s*[-*+]\s+/.test(l) || /^\s*\d+\.\s+/.test(l) || /^(-{3,}|\*{3,}|_{3,})$/.test(t) || /^\|.*\|/.test(t)) break;
      para.push(t);
      i += 1;
    }
    blocks.push(
      <p key={`p${k++}`} className="text-sm leading-relaxed my-1.5">
        {para.map((pl, pi) => (
          <React.Fragment key={pi}>{pi > 0 && <br />}{renderInline(pl, `p${k}${pi}`)}</React.Fragment>
        ))}
      </p>
    );
  }

  return <div className={className}>{blocks}</div>;
}
